import React, { useEffect, useState } from 'react'
import { AlertCircle, X, Plus, Save } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import crudService, { CrudEntity, CreateCrudData, CrudField } from '../../services/crudService'

// Extendemos la interfaz CrudField para incluir propiedades adicionales
type ExtendedCrudField = CrudField & {
  id?: string
  precision?: number
  scale?: number
}

interface EditCrudModalProps {
  isOpen: boolean
  onClose: () => void
  crud: CrudEntity | null
}

const EditCrudModal: React.FC<EditCrudModalProps> = ({ isOpen, onClose, crud }) => {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<Partial<Omit<CreateCrudData, 'fields'> & { fields?: ExtendedCrudField[] }>>({})

  // Estado para relaciones existentes y edición local
  const [existingRelations, setExistingRelations] = useState<Array<{ id: number; crudId: number; fieldName: string; relatedTable: string; displayField: string; isActive: boolean }>>([])
  const [relationDraft, setRelationDraft] = useState<Record<string, { id?: number; enabled: boolean; relatedTable: string; displayField: string }>>({})
  const [availableTables, setAvailableTables] = useState<string[]>([])

  useEffect(() => {
    if (crud) {
      setFormData({
        name: crud.name,
        table_name: crud.table_name,
        description: crud.description,
        nombre_menu: (crud as any).nombre_menu,
        has_api: crud.has_api,
        has_frontend: crud.has_frontend,
        permissions: { ...crud.permissions },
        // Preservar IDs reales de campos existentes, solo generar para campos sin ID
        fields: crud.fields.map((f, idx) => ({
          ...f,
          id: f.id ?? `new-${Date.now()}-${idx}`
        }))
      })
    }
  }, [crud])

  // Cargar relaciones existentes y tablas disponibles cuando se abra el modal
  useEffect(() => {
    const load = async () => {
      if (!crud?.id) return
      try {
        const [rels, tables] = await Promise.all([
          crudService.getRelationsByCrudId(crud.id),
          crudService.getAvailableTables().catch(() => [])
        ])
        setExistingRelations(rels || [])
        setAvailableTables(tables || [])

        // Construir draft por campo _id
        const draft: Record<string, { id?: number; enabled: boolean; relatedTable: string; displayField: string }> = {}
        const fields = (crud.fields || []) as CrudField[]
        fields
          .filter(f => f.name.endsWith('_id') && f.name !== 'id')
          .forEach(f => {
            const found = (rels || []).find(r => r.fieldName === f.name)
            draft[f.name] = {
              id: found?.id,
              // Si no existe relación previa, por defecto DESHABILITADA
              enabled: found ? found.isActive !== false : false,
              relatedTable: found?.relatedTable || f.name.replace('_id', 's'),
              displayField: found?.displayField || 'descripcion'
            }
          })
        setRelationDraft(draft)
      } catch (e: any) {
        console.warn('No se pudieron cargar relaciones/tablas:', e)
      }
    }
    if (isOpen) load()
  }, [isOpen, crud])

  // Función mejorada para eliminar recursivamente la propiedad 'property' de cualquier objeto
  const removePropertyRecursively = (obj: any): any => {
    if (!obj || typeof obj !== 'object') return obj;
    
    // Si es un array, aplicar a cada elemento
    if (Array.isArray(obj)) {
      return obj.map(item => removePropertyRecursively(item));
    }
    
    // Si es un objeto, crear una copia limpia sin la propiedad 'property'
    const result: any = {};
    
    // Copiar solo las propiedades que no son 'property'
    for (const key in obj) {
      if (key !== 'property') {
        // Aplicar recursivamente a valores que son objetos
        if (obj[key] && typeof obj[key] === 'object') {
          result[key] = removePropertyRecursively(obj[key]);
        } else {
          result[key] = obj[key];
        }
      }
    }
    
    return result;
  };

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!crud) throw new Error('No CRUD to update')
      
      // Crear payload que coincida exactamente con UpdateCrudConfigDto del backend
      const cleanFormData = {
        name: formData.name,
        table_name: formData.table_name,
        description: formData.description,
        nombre_menu: (formData as any).nombre_menu,
        has_api: formData.has_api,
        has_frontend: formData.has_frontend,
        permissions: formData.permissions,
        fields: formData.fields?.map((field: any) => {
          // Solo propiedades permitidas por CrudFieldDto
          const cleanField: any = {
            name: field.name,
            type: field.type,
            required: field.required,
            is_primary_key: field.is_primary_key,
            is_searchable: field.is_searchable,
            show_in_list: field.show_in_list,
            show_in_form: field.show_in_form !== false,
          }
          
          // Incluir ID para campos existentes (permite detectar renombrados)
          if (field.id) {
            cleanField.id = field.id
          }
          
          // Propiedades opcionales
          if (field.max_length !== undefined) cleanField.max_length = field.max_length
          if (field.default_value !== undefined) cleanField.default_value = field.default_value
          if (field.select_options) cleanField.select_options = field.select_options
          
          // Para campos decimales
          if (field.type === 'decimal') {
            if (field.precision !== undefined) cleanField.precision = field.precision
            if (field.scale !== undefined) cleanField.scale = field.scale
          }
          
          return cleanField
        })
      }
      
      // 1) Actualizar la configuración del CRUD
      const updated = await crudService.updateCrud(crud.id, cleanFormData)

      // 2) Sincronizar relaciones según relationDraft
      const fieldsId = (updated.fields || []) as Array<CrudField>
      const relationFields = fieldsId.filter(f => f.name.endsWith('_id') && f.name !== 'id').map(f => f.name)

      // Conjuntos para detectar eliminaciones
      const activeDesired = new Set<string>()

      for (const fieldName of relationFields) {
        const cfg = relationDraft[fieldName]
        if (!cfg) continue
        const tableExists = !!availableTables?.includes(cfg.relatedTable)
        if (cfg.enabled && tableExists) activeDesired.add(fieldName)

        const existing = existingRelations.find(r => r.fieldName === fieldName)
        if (cfg.enabled) {
          // Validar existencia de tabla
          if (!tableExists) {
            toast.error(`La tabla "${cfg.relatedTable}" no existe. Se omitió la relación de ${fieldName}.`)
            continue
          }
          if (existing) {
            // Actualizar si cambia algo
            const needsUpdate =
              existing.relatedTable !== cfg.relatedTable ||
              existing.displayField !== cfg.displayField ||
              existing.isActive === false
            if (needsUpdate) {
              try {
                await crudService.updateRelation(existing.id, {
                  relatedTable: cfg.relatedTable,
                  displayField: cfg.displayField,
                  isActive: true,
                })
              } catch (e: any) {
                toast.error(`No se pudo actualizar la relación de ${fieldName}: ${e?.message || 'Error'}`)
              }
            }
          } else {
            // Crear
            try {
              await crudService.createRelation({
                crudId: updated.id,
                fieldName,
                relatedTable: cfg.relatedTable,
                displayField: cfg.displayField,
                isActive: true,
              })
            } catch (e: any) {
              toast.error(`No se pudo crear la relación de ${fieldName}: ${e?.message || 'Error'}`)
            }
          }
        } else if (existing) {
          // Deshabilitado: marcar relación como inactiva
          try {
            await crudService.updateRelation(existing.id, { isActive: false })
          } catch {}
        }
      }

      // 3) Desactivar relaciones que existen pero ya no están activas en draft
      for (const rel of existingRelations) {
        if (!activeDesired.has(rel.fieldName)) {
          try { await crudService.updateRelation(rel.id, { isActive: false }) } catch {}
        }
      }

      return updated
    },
    onSuccess: () => {
      // Invalidar todas las consultas relacionadas con este CRUD
      queryClient.invalidateQueries(['cruds'])
      queryClient.invalidateQueries(['crud', crud?.id])
      queryClient.invalidateQueries(['crud-data', crud?.id])
      
      // Mostrar mensaje de éxito
      toast.success('CRUD actualizado correctamente')
      
      // Cerrar el modal
      onClose()
    },
    onError: () => {
      toast.error('Error al actualizar CRUD')
    }
  })

  if (!isOpen || !crud) return null

  const handleChange = (key: keyof CreateCrudData, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }))
  }

  const addField = () => {
    const newField: CrudField = {
      id: `new-${Date.now()}`,
      name: '',
      type: 'string',
      required: false,
      is_primary_key: false,
      is_searchable: true,
      show_in_list: true
    }
    setFormData(prev => ({
      ...prev,
      fields: [ ...(prev.fields || []), newField ]
    }))
  }

  const updateField = (fieldId: string, updates: Partial<CrudField>) => {
    // Crear una copia limpia de las actualizaciones
    const cleanUpdates = { ...updates };
    
    // Eliminar la propiedad 'property' si existe usando type assertion
    if ((cleanUpdates as any).property !== undefined) {
      delete (cleanUpdates as any).property;
    }
    
    setFormData(prev => ({
      ...prev,
      fields: (prev.fields || []).map(f => {
        if (f.id === fieldId) {
          // Crear una copia del campo
          const cleanField = { ...f };
          
          // Eliminar la propiedad 'property' si existe
          if ((cleanField as any).property !== undefined) {
            delete (cleanField as any).property;
          }
          
          // Combinar el campo limpio con las actualizaciones limpias
          return { ...cleanField, ...cleanUpdates };
        }
        return f;
      })
    }));
  }

  const removeField = (fieldId: string) => {
    
    if (!formData.fields || formData.fields.length <= 1) {
      toast.error('Debe tener al menos un campo')
      return
    }
    
    const fieldsBeforeFilter = formData.fields
    const fieldsAfterFilter = fieldsBeforeFilter.filter(f => f.id !== fieldId)
    
    
    setFormData(prev => ({
      ...prev,
      fields: fieldsAfterFilter
    }))
    
    toast.success('Campo eliminado')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Editar CRUD</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Información básica */}
          <div>
            <h3 className="text-md font-medium text-gray-900 mb-4">Información básica</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  className="input w-full text-sm"
                  value={formData.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nombre del menú (opcional)</label>
                <input
                  type="text"
                  className="input w-full text-sm"
                  value={(formData as any).nombre_menu || ''}
                  onChange={(e) => handleChange('nombre_menu' as any, e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Tabla *</label>
                <input
                  type="text"
                  className="input w-full text-sm"
                  value={formData.table_name || ''}
                  onChange={(e) => handleChange('table_name', e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Descripción *</label>
                <textarea
                  className="input w-full text-sm"
                  rows={3}
                  value={formData.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Campos */}
          <div>
            <h3 className="text-md font-medium text-gray-900 mb-4">Campos</h3>
            <div className="flex justify-end mb-3">
              <button onClick={addField} className="btn btn-primary btn-sm flex items-center" type="button">
                <Plus className="h-4 w-4 mr-2" /> Agregar Campo
              </button>
            </div>
            <div className="space-y-4">
              {(formData.fields || []).map((field) => (
                <div key={field.id} className="border rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Nombre del Campo *</label>
                      <input
                        type="text"
                        value={field.name}
                        onChange={(e) => {
                          // Normalizar nombre: reemplazar espacios por guiones bajos y eliminar caracteres especiales
                          const normalizedName = e.target.value
                            .toLowerCase()
                            .replace(/\s+/g, '_')
                            .replace(/[^a-z0-9_]/g, '')
                          updateField(field.id!, { name: normalizedName })
                        }}
                        className="input w-full text-sm"
                        placeholder="nombre_campo"
                        disabled={field.is_primary_key}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Tipo *</label>
                      <select
                        value={field.type}
                        onChange={(e) => {
                          const nextType = e.target.value as CrudField['type']
                          const updates: Partial<CrudField> = { type: nextType }
                          if (nextType !== 'string') {
                            updates.max_length = undefined
                          }
                          if (nextType === 'decimal') {
                            if (field.precision == null) updates.precision = 18
                            if (field.scale == null) updates.scale = 2
                          } else {
                            updates.precision = undefined
                            updates.scale = undefined
                          }
                          updateField(field.id!, updates)
                        }}
                        className="input w-full text-sm"
                        disabled={field.is_primary_key}
                      >
                        <option value="string">VARCHAR</option>
                        <option value="number">INT</option>
                        <option value="boolean">TINYINT(1)</option>
                        <option value="date">DATE</option>
                        <option value="datetime">DATETIME</option>
                        <option value="decimal">DECIMAL</option>
                        <option value="text">TEXT</option>
                        <option value="select">ENUM (Selección)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Longitud Máxima</label>
                      <input
                        type="number"
                        value={field.max_length || ''}
                        onChange={(e) => updateField(field.id!, { max_length: e.target.value ? parseInt(e.target.value) : undefined })}
                        className="input w-full text-sm"
                        placeholder="255"
                        disabled={field.type !== 'string'}
                      />
                    </div>
                  </div>

                  {field.type === 'decimal' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Precisión</label>
                        <input
                          type="number"
                          value={field.precision ?? ''}
                          onChange={(e) => updateField(field.id!, { precision: e.target.value ? parseInt(e.target.value) : undefined })}
                          className="input w-full text-sm"
                          placeholder="18"
                          disabled={field.is_primary_key}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Escala</label>
                        <input
                          type="number"
                          value={field.scale ?? ''}
                          onChange={(e) => updateField(field.id!, { scale: e.target.value ? parseInt(e.target.value) : undefined })}
                          className="input w-full text-sm"
                          placeholder="2"
                          disabled={field.is_primary_key}
                        />
                      </div>
                    </div>
                  )}

                  {field.type === 'select' && (
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Opciones (separadas por comas)</label>
                      <input
                        type="text"
                        placeholder="Opción 1, Opción 2, Opción 3"
                        defaultValue={(field.select_options || []).join(', ')}
                        onChange={(e) => updateField(field.id!, { select_options: e.target.value.split(',').map(opt => opt.trim()).filter(Boolean) })}
                        className="input w-full text-sm"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(field.id!, { required: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-xs text-gray-700">Requerido</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.is_searchable}
                        onChange={(e) => updateField(field.id!, { is_searchable: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-xs text-gray-700">Buscable</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.show_in_list}
                        onChange={(e) => updateField(field.id!, { show_in_list: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-xs text-gray-700">En Lista</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={(field as any).show_in_form !== false}
                        onChange={(e) => {
                          const checked = e.target.checked
                          updateField(field.id!, { show_in_form: checked, required: checked ? field.required : false } as any)
                        }}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-xs text-gray-700">Form alta/edición</span>
                    </label>

                    {!field.is_primary_key && (
                      <button 
                        onClick={() => removeField(field.id!)} 
                        className="text-red-600 hover:text-red-900 p-1" 
                        title="Eliminar"
                        type="button"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          <line x1="10" x2="10" y1="11" y2="17"></line>
                          <line x1="14" x2="14" y1="11" y2="17"></line>
                        </svg>
                      </button>
                    )}
                  </div>

                  {field.is_primary_key && (
                    <div className="mt-2 flex items-center text-xs text-blue-600">
                      <AlertCircle className="h-3 w-3 mr-1" /> Este es el campo clave primaria
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Características */}
          <div>
            <h3 className="text-md font-medium text-gray-900 mb-2">Características</h3>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  checked={!!formData.has_api}
                  onChange={(e) => handleChange('has_api', e.target.checked)}
                />
                <span className="ml-2 text-sm text-gray-700">API</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  checked={!!formData.has_frontend}
                  onChange={(e) => handleChange('has_frontend', e.target.checked)}
                />
                <span className="ml-2 text-sm text-gray-700">Frontend</span>
              </label>
            </div>
          </div>

          {/* Permisos */}
          <div>
            <h3 className="text-md font-medium text-gray-900 mb-2">Permisos</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  checked={!!formData.permissions?.read}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    permissions: { ...(prev.permissions || {} as any), read: e.target.checked }
                  }))}
                />
                <span className="ml-2 text-sm text-gray-700">Leer</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  checked={!!formData.permissions?.create}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    permissions: { ...(prev.permissions || {} as any), create: e.target.checked }
                  }))}
                />
                <span className="ml-2 text-sm text-gray-700">Crear</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  checked={!!formData.permissions?.update}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    permissions: { ...(prev.permissions || {} as any), update: e.target.checked }
                  }))}
                />
                <span className="ml-2 text-sm text-gray-700">Editar</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  checked={!!formData.permissions?.delete}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    permissions: { ...(prev.permissions || {} as any), delete: e.target.checked }
                  }))}
                />
                <span className="ml-2 text-sm text-gray-700">Eliminar</span>
              </label>
            </div>
          </div>
          {/* Relaciones */}
          <div>
            <h3 className="text-md font-medium text-gray-900 mb-4">Relaciones</h3>
            <div className="space-y-3">
              {(formData.fields || [])
                .filter(f => f.name.endsWith('_id') && f.name !== 'id')
                .map((field) => {
                  const key = field.name
                  const cfg = relationDraft[key] || { enabled: false, relatedTable: key.replace('_id','s'), displayField: 'descripcion' }
                  return (
                    <div key={key} className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Campo</label>
                          <div className="text-sm text-blue-700 font-medium">{key}</div>
                          <label className="flex items-center mt-2">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-primary-600 shadow-sm"
                              checked={cfg.enabled}
                              onChange={(e) => setRelationDraft(prev => ({
                                ...prev,
                                [key]: { ...(prev[key] || {}), enabled: e.target.checked, relatedTable: prev[key]?.relatedTable || key.replace('_id','s'), displayField: prev[key]?.displayField || 'descripcion' }
                              }))}
                            />
                            <span className="ml-2 text-xs text-gray-700">Habilitar relación</span>
                          </label>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Tabla Relacionada</label>
                          <select
                            className="input w-full text-sm"
                            value={cfg.relatedTable}
                            onChange={(e) => setRelationDraft(prev => ({ ...prev, [key]: { ...(prev[key] || {}), enabled: true, relatedTable: e.target.value, displayField: prev[key]?.displayField || 'descripcion' } }))}
                            disabled={!cfg.enabled}
                          >
                            <option value={key.replace('_id','s')}>{key.replace('_id','s')} (sugerida)</option>
                            {availableTables.filter(t => !!t && t !== formData.table_name).map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Campo a Mostrar</label>
                          <input
                            type="text"
                            className="input w-full text-sm"
                            placeholder="descripcion, nombre, titulo..."
                            value={cfg.displayField}
                            onChange={(e) => setRelationDraft(prev => ({ ...prev, [key]: { ...(prev[key] || {}), enabled: true, relatedTable: prev[key]?.relatedTable || key.replace('_id','s'), displayField: e.target.value } }))}
                            disabled={!cfg.enabled}
                          />
                        </div>
                      </div>
                      {cfg.enabled && (
                        <div className="mt-2 text-xs text-gray-600">Se mostrará como select con opciones de "{cfg.relatedTable}"</div>
                      )}
                    </div>
                  )
                })}
              {!(formData.fields || []).some(f => f.name.endsWith('_id') && f.name !== 'id') && (
                <div className="text-sm text-gray-500">No se detectaron campos *_id en este CRUD.</div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} className="btn btn-secondary" disabled={updateMutation.isLoading}>
            Cancelar
          </button>
          <button
            onClick={() => updateMutation.mutate()}
            className="btn btn-primary flex items-center"
            disabled={updateMutation.isLoading}
          >
            <Save className="h-4 w-4 mr-2" />
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditCrudModal
